import FiltersOptions from '../../../../api/typedefs/sdk/PaginationOptions';
import type { RequestExecute } from '../../../../api';
import Helper from '../../../models/Helper';

import type { JobApplication } from '../../job-applications/types';
import type { Organization } from '../types/Organization';
import type { JobSearchArea } from '../types/JobSearchArea';

const URI = '/me';

/**
 * @desc Helper for current user.
 */
class MeHelper extends Helper {
  constructor(api) {
    super(api, URI);

    this.user = null;
  }

  /**
   * @desc Get information of current connected user.
   * @return {Promise<UserWithDetails>} The promise handling the request.
   * @example
   * const response = await myClient.helpers.user.me.get();
   */
  async get() {
    const response = await super.get();
    this.user = response.body;

    return response;
  }

  /**
   * @desc Update information of current connected user.
   * @param {UserData} userData user fields you want to update
   * @return {Promise} The promise handling the request.
   * @example
   * await myClient.helpers.user.me.partialUpdate({ firstName: 'John' });
   */
  async partialUpdate(userData) {
    const userId = await this.getUserId();

    return this.api.helpers.user.users.partialUpdate(userId, userData);
  }

  /**
   * @desc Get id of current connected user.
   * @return {Promise<Number>} The promise handling the request.
   * @example
   * const response = await myClient.helpers.user.me.getUserId();
   */
  async getUserId() {
    if (this.user && this.user.id) {
      return this.user.id;
    }

    await this.get();

    return this.user.id;
  }

  /**
   * @desc Get courses followed by current connected user.
   * @return {Promise<Array<CourseWithUserInformation>>} The promise handling the request.
   * @example
   * const response = await myClient.helpers.user.me.getFollowedCourses();
   */
  async getFollowedCourses() {
    const userId = await this.getUserId();
    return this.api.helpers.learningActivity.followedCourses.get(userId);
  }

  /**
   * @desc Get specific course with information from current connected user.
   * @param {Number} courseId Id of the course to get.
   * @return {Promise<CourseWithUserInformation>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.user.me.getCourse(courseId);
   */
  async getCourse(courseId) {
    const userId = await this.getUserId();
    return this.api.helpers.learningActivity.courses.get(userId, courseId);
  }

  /**
   * @desc Get table of content of specific course with information from current
   * connected user.
   * @param {Number} courseId Id of the course to get.
   * @return {Promise<TocItemWithUserInformation>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.user.me.getTableOfContent(courseId);
   */
  async getTableOfContent(courseId) {
    const userId = await this.getUserId();
    return this.api.helpers.learningActivity.courses.getTableOfContent(userId, courseId);
  }

  /**
   * @desc Get path currently followed by current connected user.
   * @return {Promise<PathWithUserInformation>} The promise handling the request.
   * @example
   * const response = await myClient.helpers.user.me.getFollowedPath();
   */
  async getFollowedPath() {
    const userId = await this.getUserId();
    return this.api.helpers.learningActivity.followedPath.get(userId);
  }

  /**
   * @desc Find projects of path currently followed by current connected user.
   * @param {String} [status=''] If provide, request only projects with given status.
   * @return {Promise<Array<ProjectWithUserInformation>>} The promise handling the request.
   * @example
   * const response = await myClient.helpers.user.me.findFollowedPathProjects();
   * @example
   * const status = 'in-progress';
   * const response = await myClient.helpers.user.me.findFollowedPathProjects(status);
   */
  async findFollowedPathProjects(status = '') {
    const userId = await this.getUserId();
    return this.api.helpers.learningActivity.followedPath.findProjects(userId, status);
  }

  /**
   * @desc Find paths which have been followed by current connected user.
   * @param {PaginationOptions} [filters={}] Filters to use to find paths.
   * @return {Promise<Array<PathWithUserInformation>>} The promise handling the request.
   * @example
   * const response = await myClient.helpers.user.me.findPaths();
   * @example
   * const filters = { start: 0, end: 5 };
   * const response = await myClient.helpers.user.me.findPaths(filters);
   */
  async findPaths(filters = {}) {
    const userId = await this.getUserId();
    return this.api.helpers.learningActivity.paths.find(userId, filters);
  }

  /**
   * @desc Get learning outcomes of specific course with information from current connected user.
   * @param {Number} courseId Id of the course to get.
   * @return {Promise<Array<LearningOutcomeWithUserInformation>>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.user.me.getCourseLearningOutcomes(courseId);
   */
  async getCourseLearningOutcomes(courseId) {
    const userId = await this.getUserId();
    return this.api.helpers.learningActivity.courses.getLearningOutcomes(userId, courseId);
  }

  /**
   * @desc Make current connected user following given course.
   * @param {Number} courseId Id of the course to follow.
   * @return {Promise} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.user.me.followCourse(courseId);
   */
  async followCourse(courseId) {
    const userId = await this.getUserId();
    return this.api.helpers.learningActivity.courses.follow(userId, courseId);
  }

  /**
   * @desc Get project with information from current connected user.
   * @param {Number} projectId Id of the project to get.
   * @return {Promise<ProjectWithUserInformation>} The promise handling the request.
   * @example
   * const projectId = 1234;
   * const response = await myClient.helpers.user.me.getProject(projectId);
   */
  async getProject(projectId) {
    const userId = await this.getUserId();
    return this.api.helpers.user.users.id(userId).projects.get(projectId);
  }

  /**
   * @desc Get sessions of specific project from current connected user.
   * @param {Number} projectId Id of the project to get.
   * @return {Promise<ProjectSession>} The promise handling the request.
   * @example
   * const projectId = 1234;
   * const response = await myClient.helpers.user.me.getProjectSessions(projectId);
   */
  async getProjectSessions(projectId) {
    const userId = await this.getUserId();
    return this.api.helpers.user.users.id(userId).projects.id(projectId).projectSessions.get();
  }

  /**
   * @desc Get interests for current connected user.
   * @return {Promise<Interest[]>} The promise handling the request.
   * @example
   * const response = await myClient.helpers.user.me.getInterests();
   */
  async getInterests() {
    const userId = await this.getUserId();
    return this.api.helpers.user.users.getInterests(userId);
  }

  /**
   * @desc Get teams of current connected user.
   * @return {Promise<Interest[]>} The promise handling the request.
   * @example
   * const response = await myClient.helpers.user.me.findTeams();
   */
  async findTeams(params) {
    const userId = await this.getUserId();
    return this.api.helpers.user.users.id(userId).teams.find(params);
  }

  async getOrganizationPermissions(
    organizationId: string
  ): RequestExecute<{ permissions: Array<Record<'code', string>> }> {
    const request = super.get(organizationId, false);
    request.url += '/organizations/:id/permissions';

    return request.execute();
  }

  async getTeamsPermissions(): RequestExecute<{
    teams: Record<'all' | number, { permissions: Array<Record<'code', string>> }>;
  }> {
    const request = super.get('', false);
    request.url += '/teams-permissions';

    return request.execute();
  }

  async getOnboardingsPermissions(): RequestExecute<{
    onboardings: Record<'all' | number, { permissions: Array<Record<'code', string>> }>;
  }> {
    const request = super.get('', false);
    request.url += '/onboardings-permissions';

    return request.execute();
  }

  async getPermissions(): RequestExecute<{
    permissions: Array<Record<'code', string>>;
  }> {
    const request = super.get('', false);
    request.url += '/permissions';

    return request.execute();
  }

  async getPrivacySettings() {
    const userId = await this.getUserId();
    return this.api.helpers.user.users.getPrivacySettings(userId);
  }

  async partialUpdatePrivacySettings(privacySetting, { context }: { context?: string } = {}) {
    const userId = await this.getUserId();
    return this.api.helpers.user.users.partialUpdatePrivacySettings(userId, privacySetting, {
      context,
    });
  }

  async getJobSearchAreas(): Promise<JobSearchArea[]> {
    const userId = await this.getUserId();
    return this.api.helpers.user.users.getJobSearchAreas(userId);
  }

  async updateJobSearchAreas(
    jobSearchAreas: JobSearchArea[],
    { context }: { context?: string } = {}
  ) {
    const userId = await this.getUserId();
    return this.api.helpers.user.users.updateJobSearchAreas(userId, jobSearchAreas, {
      context,
    });
  }

  async getOrganization(): Promise<Organization> {
    const userId = await this.getUserId();
    return this.api.helpers.user.users.getOrganization(userId);
  }

  async getJobApplications(filters: FiltersOptions): Promise<JobApplication> {
    const userId = await this.getUserId();
    return this.api.helpers.user.users.getJobApplications(userId, filters);
  }
}

export default MeHelper;
